// frontend/src/hooks/useUsers.ts - COMPLETELY UPDATED WITH ALL FIXES
// Entity-specific hooks for users with proper backend integration

import { useCallback } from 'react';
import apiClient from '@/api';
import { useAuthStore } from '@/store/auth';
import { 
  useApi, 
  useMutation, 
  validateObjectId, 
  validateObjectIds, 
  normalizeSingleResponse, 
  normalizePaginatedResponse,
  type UseApiOptions,
  type UseMutationOptions
} from './useApi';

// Import only types from API - not functions
import type {
  // Response types
  StandardResponse,
  PaginatedResponse,
  
  // User types
  User,
  UserListResponse,
  UserCreateRequest,
  UserUpdateRequest,
  UsersQueryParams,
  
  // Query parameter types
  QueryParams
} from '@/api/types';

// ===== BACKEND RESPONSE INTERFACES =====

/**
 * Backend users list response format (based on users.js analysis)
 */
interface BackendUserListResponse {
  users: User[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
  analytics?: {
    totalUsers: number;
    adminCount: number;
    contributorCount: number;
    implementorCount: number;
    activeUsers: number;
    deactivatedUsers: number;
    usersWithEmailAccounts: number;
  };
  filters?: {
    search?: string;
    role?: string;
    status?: string;
    organizationId?: string;
    sortBy?: string;
    sortOrder?: string;
  };
  permissions?: {
    canCreateUsers: boolean;
    canEditUsers: boolean;
    canDeleteUsers: boolean;
  };
}

/**
 * Backend single user response format
 */
interface BackendUserResponse {
  success?: boolean;
  data?: User;
  user?: User;
  message?: string;
  error?: string;
  permissions?: {
    canEdit: boolean;
    canDelete: boolean;
    canViewDetails: boolean;
  };
}

/**
 * Backend user operation response format
 */
interface BackendUserOperationResponse {
  success: boolean;
  message: string;
  user?: User;
  data?: User;
  temporaryPassword?: string;
}

// ===== TYPE GUARDS =====

function isBackendUserListResponse(response: any): response is BackendUserListResponse {
  return response && 
    typeof response === 'object' && 
    Array.isArray(response.users) && 
    response.pagination &&
    typeof response.pagination.page === 'number';
}

function isBackendUserResponse(response: any): response is BackendUserResponse {
  return response && 
    typeof response === 'object' && 
    (response.data || response.user || response._id || response.username);
}

function isBackendUserOperationResponse(response: any): response is BackendUserOperationResponse {
  return response && 
    typeof response === 'object' && 
    typeof response.success === 'boolean' &&
    typeof response.message === 'string';
}

// ===== USER QUERY HOOKS =====

/**
 * Fetch paginated list of users with security validation
 */
export const useUsers = (params?: UsersQueryParams, options?: UseApiOptions) => {
  const { user: currentUser } = useAuthStore();
  
  return useApi(
    async (): Promise<PaginatedResponse<User>> => {
      
      // Security validation for organization-specific queries
      let finalParams = { ...params };
      
      if (finalParams.organizationId) {
        
        // Only implementors can query across organizations
        if (currentUser?.role !== 'implementor') {
          let userOrgId: string | undefined;
          
          if (currentUser?.organization) {
            if (typeof currentUser.organization === 'string') {
              userOrgId = currentUser.organization;
            } else if ((currentUser.organization as any)._id) {
              userOrgId = (currentUser.organization as any)._id;
            }
          }
          
          if (userOrgId && finalParams.organizationId !== userOrgId) {
            console.warn('useUsers: Security - User attempting to access different organization users');
            throw new Error('Access denied: You can only view users from your own organization');
          }
        }
        
        validateObjectId(finalParams.organizationId, 'Organization ID');
      }
      
      const response = await apiClient.users.list(finalParams);
      
      // CRITICAL FIX: Handle backend response format transformation
      if (isBackendUserListResponse(response)) {
        
        const transformedResponse: PaginatedResponse<User> = {
          data: response.users,
          pagination: {
            currentPage: response.pagination.page,
            totalPages: response.pagination.totalPages,
            totalItems: response.pagination.total,
            itemsPerPage: response.pagination.limit,
            hasNextPage: response.pagination.hasNextPage,
            hasPrevPage: response.pagination.hasPrevPage
          },
          total: response.pagination.total,
          totalPages: response.pagination.totalPages,
          page: response.pagination.page,
          limit: response.pagination.limit,
          analytics: response.analytics,
          filters: response.filters,
          metadata: {
            permissions: response.permissions
          }
        };
        
        return transformedResponse;
      }
      
      // Fallback to normalization if response format is unexpected
      return normalizePaginatedResponse<User>(response as UserListResponse);
    },
    {
      immediate: true,
      dependencies: params ? [JSON.stringify(params)] : [],
      rateLimitKey: 'users_list',
      ...options
    }
  );
};

/**
 * Fetch single user by ID with comprehensive handling
 */
export const useUser = (id?: string, options?: UseApiOptions) => {
  return useApi(
    async (): Promise<User> => {
      validateObjectId(id, 'User ID');
      
      
      const response = await apiClient.users.get(id!);
      
      // Handle different backend response formats
      if (isBackendUserResponse(response)) {
        let user: User | undefined;
        
        if (response.data) {
          // StandardResponse<User> format: { data: User }
          user = response.data;
        } else if (response.user) {
          // Alternative format: { user: User }
          user = response.user;
        } else if ((response as any)._id || (response as any).username) {
          // Direct user object
          user = response as User;
        }
        
        if (!user) {
          console.error('useUser: Could not extract user from response:', response);
          throw new Error('Invalid user data received from server');
        }
        
        // Validate user object
        if (!user._id && !user.id) {
          throw new Error('User object missing required ID field');
        }
        
        return user;
      }
      
      // Fallback to normalization
      return normalizeSingleResponse<User>(response);
    },
    {
      immediate: !!id,
      rateLimitKey: `user_${id}`,
      ...options
    }
  );
};

/**
 * Fetch users for a specific organization with validation
 */
export const useOrganizationUsers = (organizationId?: string, params?: UsersQueryParams, options?: UseApiOptions) => {
  const { user: currentUser } = useAuthStore();
  
  return useApi(
    async (): Promise<PaginatedResponse<User>> => {
      if (!organizationId) {
        return {
          data: [],
          pagination: {
            currentPage: 1,
            totalPages: 0,
            totalItems: 0,
            itemsPerPage: 10,
            hasNextPage: false,
            hasPrevPage: false
          },
          total: 0,
          totalPages: 0,
          page: 1,
          limit: 10
        };
      }
      
      validateObjectId(organizationId, 'Organization ID');
      
      // Security validation - only implementors can query across organizations
      if (currentUser?.role !== 'implementor') {
        let userOrgId: string | undefined;
        
        if (currentUser?.organization) {
          if (typeof currentUser.organization === 'string') {
            userOrgId = currentUser.organization;
          } else if ((currentUser.organization as any)._id) {
            userOrgId = (currentUser.organization as any)._id;
          }
        }
        
        if (userOrgId && organizationId !== userOrgId) {
          console.warn('useOrganizationUsers: Security - User attempting to access different organization users');
          throw new Error('Access denied: You can only view users from your own organization');
        }
      }
      
      
      const finalParams: UsersQueryParams = {
        ...params,
        organizationId: organizationId
      };
      
      const response = await apiClient.users.list(finalParams);
      
      // Handle backend response format
      if (isBackendUserListResponse(response)) {
        const transformedResponse: PaginatedResponse<User> = {
          data: response.users,
          pagination: {
            currentPage: response.pagination.page,
            totalPages: response.pagination.totalPages,
            totalItems: response.pagination.total,
            itemsPerPage: response.pagination.limit,
            hasNextPage: response.pagination.hasNextPage,
            hasPrevPage: response.pagination.hasPrevPage
          },
          total: response.pagination.total,
          totalPages: response.pagination.totalPages,
          page: response.pagination.page,
          limit: response.pagination.limit,
          analytics: response.analytics,
          filters: response.filters
        };
        
        return transformedResponse;
      }
      
      return normalizePaginatedResponse<User>(response as UserListResponse);
    },
    {
      immediate: !!organizationId,
      dependencies: [organizationId, JSON.stringify(params || {})],
      rateLimitKey: `organization_users_${organizationId}`,
      ...options
    }
  );
};

/**
 * Fetch user email accounts with proper error handling
 */
export const useUserEmailAccounts = (userId?: string, options?: UseApiOptions) => {
  return useApi(
    async () => {
      validateObjectId(userId, 'User ID');
      
      
      // Email accounts are managed through auth API, not users API
      const response = await apiClient.auth.getEmailAccounts(userId);
      
      return normalizeSingleResponse(response);
    },
    {
      immediate: !!userId,
      rateLimitKey: `user_email_accounts_${userId}`,
      ...options
    }
  );
};

/**
 * Fetch user statistics with proper handling
 */
export const useUserStats = (userId?: string, options?: UseApiOptions) => {
  return useApi(
    async (): Promise<any> => {
      validateObjectId(userId, 'User ID');
      
      
      // Check if getStats method exists
      if (!apiClient.users.getStats) {
        throw new Error('User statistics endpoint not available');
      }
      
      const response = await apiClient.users.getStats(userId!);
      
      return normalizeSingleResponse(response);
    },
    {
      immediate: false, // Manual trigger for stats
      rateLimitKey: `user_stats_${userId}`,
      ...options
    }
  );
};

/**
 * Fetch user activity history with pagination
 */
export const useUserActivity = (userId?: string, params?: QueryParams, options?: UseApiOptions) => {
  return useApi(
    async (): Promise<PaginatedResponse<any>> => {
      validateObjectId(userId, 'User ID');
      
      
      // Check if getActivity method exists
      if (!apiClient.users.getActivity) {
        throw new Error('User activity endpoint not available');
      }
      
      const response = await apiClient.users.getActivity(userId!, params);
      
      return normalizePaginatedResponse(response);
    },
    {
      immediate: !!userId,
      dependencies: [userId, JSON.stringify(params || {})],
      rateLimitKey: `user_activity_${userId}`,
      ...options
    }
  );
};

// ===== USER MUTATION HOOKS =====

/**
 * Create new user with comprehensive validation
 */
export const useCreateUser = (options?: UseMutationOptions<User, UserCreateRequest>) => {
  return useMutation(
    async (data: UserCreateRequest): Promise<User> => {
      // Validation
      if (!data.username?.trim()) {
        throw new Error('Username is required');
      }

      if (!data.role) {
        throw new Error('Role is required');
      }

      if (!data.organizationId?.trim()) {
        throw new Error('Organization is required');
      }

      // Email format validation (only if email is provided)
      if (data.email && data.email.trim()) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(data.email)) {
          throw new Error('Please enter a valid email address');
        }
      }

      // Phone number format validation (only if phone number is provided)
      if (data.phoneNumber && data.phoneNumber.trim()) {
        const digitsOnly = data.phoneNumber.replace(/\D/g, '');
        if (!/^[\d\s\-\+\(\)]+$/.test(data.phoneNumber)) {
          throw new Error('Phone number can only contain digits, spaces, and +()-');
        } else if (digitsOnly.length < 10) {
          throw new Error('Phone number must be at least 10 digits');
        }
      }
      
      // Username format validation
      const usernameRegex = /^[a-zA-Z0-9._-]{3,30}$/;
      if (!usernameRegex.test(data.username)) {
        throw new Error('Username must be 3-30 characters and contain only letters, numbers, periods, underscores, and hyphens');
      }
      
      validateObjectId(data.organizationId, 'Organization ID');
      
      
      const response = await apiClient.users.create(data);
      
      // Handle backend response format
      if (isBackendUserOperationResponse(response)) {
        if (!response.success) {
          throw new Error(response.message || 'Failed to create user');
        }
        
        const user = response.user || response.data;
        if (!user) {
          throw new Error('User data not returned from server');
        }
        
        return user;
      }
      
      return normalizeSingleResponse<User>(response);
    },
    options
  );
};

/**
 * Update existing user with validation
 */
export const useUpdateUser = (options?: UseMutationOptions<User, { id: string; data: UserUpdateRequest }>) => {
  return useMutation(
    async ({ id, data }: { id: string; data: UserUpdateRequest }): Promise<User> => {
      validateObjectId(id, 'User ID');
      
      // Email validation if provided
      if (data.email?.trim()) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(data.email)) {
          throw new Error('Please enter a valid email address');
        }
      }

      // Phone number validation if provided
      if (data.phoneNumber && data.phoneNumber.trim()) {
        const digitsOnly = data.phoneNumber.replace(/\D/g, '');
        if (!/^[\d\s\-\+\(\)]+$/.test(data.phoneNumber)) {
          throw new Error('Phone number can only contain digits, spaces, and +()-');
        } else if (digitsOnly.length < 10) {
          throw new Error('Phone number must be at least 10 digits');
        }
      }

      // Username validation if provided
      if (data.username?.trim()) {
        const usernameRegex = /^[a-zA-Z0-9._-]{3,30}$/;
        if (!usernameRegex.test(data.username)) {
          throw new Error('Username must be 3-30 characters and contain only letters, numbers, periods, underscores, and hyphens');
        }
      }
      
      // Validate organizationId if provided (some UserUpdateRequest interfaces may not include this)
      if ((data as any).organizationId) {
        validateObjectId((data as any).organizationId, 'Organization ID');
      }
      
      
      const response = await apiClient.users.update(id, data);
      
      // Handle backend response format
      if (isBackendUserOperationResponse(response)) {
        if (!response.success) {
          throw new Error(response.message || 'Failed to update user');
        }
        
        const user = response.user || response.data;
        if (!user) {
          throw new Error('User data not returned from server');
        }
        
        return user;
      }
      
      return normalizeSingleResponse<User>(response);
    },
    options
  );
};

/**
 * Delete user with proper error handling
 */
export const useDeleteUser = (options?: UseMutationOptions<void, string>) => {
  return useMutation(
    async (userId: string): Promise<void> => {
      validateObjectId(userId, 'User ID');
      
      
      const response = await apiClient.users.delete(userId);
      
      // Handle backend response format
      if (isBackendUserOperationResponse(response)) {
        if (!response.success) {
          throw new Error(response.message || 'Failed to delete user');
        }
      }
      
    },
    options
  );
};

/**
 * Activate user with proper response handling
 */
export const useActivateUser = (options?: UseMutationOptions<User, string>) => {
  return useMutation(
    async (userId: string): Promise<User> => {
      validateObjectId(userId, 'User ID');
      
      
      const response = await apiClient.users.activate(userId);
      
      // Handle backend response format
      if (isBackendUserOperationResponse(response)) {
        if (!response.success) {
          throw new Error(response.message || 'Failed to activate user');
        }
        
        const user = response.user || response.data;
        if (!user) {
          throw new Error('User data not returned from server');
        }
        
        return user;
      }
      
      return normalizeSingleResponse<User>(response);
    },
    options
  );
};

/**
 * Deactivate user with proper response handling
 */
export const useDeactivateUser = (options?: UseMutationOptions<User, string>) => {
  return useMutation(
    async (userId: string): Promise<User> => {
      validateObjectId(userId, 'User ID');
      
      
      const response = await apiClient.users.deactivate(userId);
      
      // Handle backend response format
      if (isBackendUserOperationResponse(response)) {
        if (!response.success) {
          throw new Error(response.message || 'Failed to deactivate user');
        }
        
        const user = response.user || response.data;
        if (!user) {
          throw new Error('User data not returned from server');
        }
        
        return user;
      }
      
      return normalizeSingleResponse<User>(response);
    },
    options
  );
};

/**
 * Reset user password with temporary password handling
 */
export const useResetUserPassword = (options?: UseMutationOptions<{ temporaryPassword: string }, { userId: string; newPassword?: string; generatePassword?: boolean }>) => {
  return useMutation(
    async ({ userId, newPassword, generatePassword }: { userId: string; newPassword?: string; generatePassword?: boolean }): Promise<{ temporaryPassword: string }> => {
      validateObjectId(userId, 'User ID');


      const response = await apiClient.users.resetPassword(userId, { newPassword, generatePassword });

      // Handle backend response format
      if (isBackendUserOperationResponse(response)) {
        if (!response.success) {
          throw new Error(response.message || 'Failed to reset user password');
        }

        if (response.temporaryPassword) {
          return { temporaryPassword: response.temporaryPassword };
        }
      }

      return normalizeSingleResponse<{ temporaryPassword: string }>(response);
    },
    options
  );
};

/**
 * Add email account to user
 */
export const useAddUserEmailAccount = (options?: UseMutationOptions) => {
  return useMutation(
    async (data: { userId: string; emailAccountData: any }) => {
      validateObjectId(data.userId, 'User ID');
      
      
      // Email account management is handled through auth API
      // Check if the method exists before calling it
      if (!apiClient.auth || !('addEmailAccount' in apiClient.auth)) {
        throw new Error('Add email account endpoint not available');
      }
      
      const response = await (apiClient.auth as any).addEmailAccount(data.userId, data.emailAccountData);
      
      return normalizeSingleResponse(response);
    },
    options
  );
};

/**
 * Remove email account from user
 */
export const useRemoveUserEmailAccount = (options?: UseMutationOptions) => {
  return useMutation(
    async (data: { userId: string; email: string }) => {
      validateObjectId(data.userId, 'User ID');
      
      if (!data.email || !data.email.trim()) {
        throw new Error('Email address is required');
      }
      
      
      // Email account removal is handled through auth API
      const response = await apiClient.auth.removeEmailAccount(data.userId, data.email);
      
      return normalizeSingleResponse(response);
    },
    options
  );
};

/**
 * Batch user operations (placeholder for future implementation)
 */
export const useBatchUserOperations = (options?: UseMutationOptions) => {
  return useMutation(
    async (data: {
      operation: 'activate' | 'deactivate' | 'delete' | 'update';
      userIds: string[];
      updateData?: Partial<UserUpdateRequest>;
    }) => {
      if (!data.userIds || data.userIds.length === 0) {
        throw new Error('At least one user ID is required');
      }
      
      validateObjectIds(data.userIds, 'User IDs');
      
      
      // Batch operations are not available in the current users API
      throw new Error('Batch user operations not available in current API version');
    },
    options
  );
};

// ===== SEARCH AND ANALYTICS =====

/**
 * Search users with advanced filtering and proper response handling
 */
export const useSearchUsers = (query?: string, filters?: any, options?: UseApiOptions) => {
  return useApi(
    async (): Promise<PaginatedResponse<User>> => {
      if (!query?.trim()) {
        return {
          data: [],
          pagination: {
            currentPage: 1,
            totalPages: 0,
            totalItems: 0,
            itemsPerPage: 10,
            hasNextPage: false,
            hasPrevPage: false
          },
          total: 0,
          totalPages: 0,
          page: 1,
          limit: 10
        };
      }
      
      
      // Check if search endpoint exists, otherwise fall back to list with search params
      if (apiClient.users && 'search' in apiClient.users) {
        const response = await (apiClient.users as any).search(query, filters);
        
        // Handle backend response format
        if (isBackendUserListResponse(response)) {
          const transformedResponse: PaginatedResponse<User> = {
            data: response.users,
            pagination: {
              currentPage: response.pagination.page,
              totalPages: response.pagination.totalPages,
              totalItems: response.pagination.total,
              itemsPerPage: response.pagination.limit,
              hasNextPage: response.pagination.hasNextPage,
              hasPrevPage: response.pagination.hasPrevPage
            },
            total: response.pagination.total,
            totalPages: response.pagination.totalPages,
            page: response.pagination.page,
            limit: response.pagination.limit
          };
          
          return transformedResponse;
        }
        
        return normalizePaginatedResponse<User>(response);
      } else {
        // Fallback to list with search parameter
        const searchParams: UsersQueryParams = {
          search: query,
          ...filters
        };
        
        const response = await apiClient.users.list(searchParams);
        
        // Handle backend response format
        if (isBackendUserListResponse(response)) {
          const transformedResponse: PaginatedResponse<User> = {
            data: response.users,
            pagination: {
              currentPage: response.pagination.page,
              totalPages: response.pagination.totalPages,
              totalItems: response.pagination.total,
              itemsPerPage: response.pagination.limit,
              hasNextPage: response.pagination.hasNextPage,
              hasPrevPage: response.pagination.hasPrevPage
            },
            total: response.pagination.total,
            totalPages: response.pagination.totalPages,
            page: response.pagination.page,
            limit: response.pagination.limit
          };
          
          return transformedResponse;
        }
        
        return normalizePaginatedResponse<User>(response as UserListResponse);
      }
    },
    {
      immediate: !!query?.trim(),
      dependencies: [query, JSON.stringify(filters || {})],
      debounce: 300,
      retryCount: 2,
      rateLimitKey: 'search_users',
      ...options
    }
  );
};

/**
 * User analytics and reporting (placeholder for future implementation)
 */
export const useUserAnalytics = (params?: any, options?: UseApiOptions) => {
  return useApi(
    async () => {
      
      // User analytics endpoint is not available in the current API
      throw new Error('User analytics endpoint not available in current API version');
    },
    {
      immediate: false, // Manual trigger for analytics
      dependencies: params ? [JSON.stringify(params)] : [],
      retryCount: 1,
      rateLimitKey: 'user_analytics',
      ...options
    }
  );
};

// ===== EXPORTS =====

// All hooks are already exported above with individual export statements
// No need to re-export them here

// Default export for convenience
const userHooks = {
  // Query hooks
  useUsers,
  useUser,
  useOrganizationUsers,
  useUserEmailAccounts,
  useUserStats,
  useUserActivity,
  
  // Mutation hooks
  useCreateUser,
  useUpdateUser,
  useDeleteUser,
  useActivateUser,
  useDeactivateUser,
  useResetUserPassword,
  useAddUserEmailAccount,
  useRemoveUserEmailAccount,
  useBatchUserOperations,
  
  // Search and analytics
  useSearchUsers,
  useUserAnalytics
};

export default userHooks;